/**
 * AggregateFieldTemplates — aggregate-function templates offered by the
 * HAVING filter chip's field step (Plan 3 decision 4). The "Aggregates" group
 * leads the picker only when the chip serves the HAVING clause (`clause ===
 * 'having'`): `COUNT(*)` is zero-arg and commits its field text directly;
 * `COUNT` / `SUM` / `AVG` / `MIN` / `MAX` are column-parameterized — picking
 * one re-shows the SAME standard column list (`useVqeFilterColumns` items)
 * for the argument, then wraps the resolved argument column's own
 * `quotedField` (the chip's NORMAL column-ref emit — never hand-quoted here)
 * inside `FN(…)`.
 *
 * The synthesized field's `dataType` drives operator / value-input
 * categorization only (never emitted): `COUNT` / `SUM` / `AVG` produce a
 * number whatever they aggregate (an opaque 'numeric' marker → the numeric
 * comparison set), while `MIN` / `MAX` return a value OF THE ARGUMENT'S TYPE —
 * `MAX(o.created)` is a timestamp and gets the date input, `MIN(name)` a text
 * one. Only the type is inherited: no FK / enum / JSON / owning-table identity
 * carries over (an aggregate is not a column — no lookup, no MetaInfo
 * declaration applies). No schema/table backs the synthetic column
 * (`jsonContext` stays undefined), so JSON/enum layering never applies.
 *
 * Deliberately NOT the full `AGGREGATE_FUNCTION_NAMES` set
 * (`detection/aggregateAwareness.ts`) — this is a curated 6-template starter
 * list, not every aggregate the SQL dialect supports.
 */

import { defaultDraftConditionFields } from '../../../common/utils/filterUtils';
import type { FilterGroup, TableExplorerColumn } from '../../../core/types/workspaceTypes';
import { vqeCopy } from '../../ux/vqeCopy';
import type { CommandPickerGroup } from './CommandPickerPopover';
import type { ResolvedFilterColumn } from './useVqeFilterColumns';

export type AggregateFnName = 'COUNT' | 'SUM' | 'AVG' | 'MIN' | 'MAX';

export interface AggregateTemplate {
  /** Stable picker-item key (e.g. `agg-count-star`, `agg-sum`). */
  readonly key: string;
  readonly fn: AggregateFnName;
  /** True only for the zero-arg `COUNT(*)` template. */
  readonly zeroArg: boolean;
}

export const AGGREGATE_TEMPLATES: readonly AggregateTemplate[] = [
  { key: 'agg-count-star', fn: 'COUNT', zeroArg: true },
  { key: 'agg-count', fn: 'COUNT', zeroArg: false },
  { key: 'agg-sum', fn: 'SUM', zeroArg: false },
  { key: 'agg-avg', fn: 'AVG', zeroArg: false },
  { key: 'agg-min', fn: 'MIN', zeroArg: false },
  { key: 'agg-max', fn: 'MAX', zeroArg: false },
];

/** Resolve a picker item key back to its aggregate template, or undefined. */
export function findAggregateTemplate(key: string): AggregateTemplate | undefined {
  return AGGREGATE_TEMPLATES.find((t) => t.key === key);
}

/**
 * Builds the "Aggregates" picker group — rendered FIRST, above the standard
 * column groups, only when the chip's field step serves HAVING.
 */
export function buildAggregatesGroup(): CommandPickerGroup {
  return {
    heading: vqeCopy.picker.aggregatesHeading,
    items: AGGREGATE_TEMPLATES.map((t) => {
      const label = t.zeroArg ? `${t.fn}(*)` : `${t.fn}(column…)`;
      return { key: t.key, value: label, label };
    }),
  };
}

/**
 * The categorization type of `fn(argument)`: MIN/MAX return their argument's
 * own type; COUNT/SUM/AVG (and a bare `COUNT(*)`) are numeric.
 */
export function aggregateResultDataType(
  fn: AggregateFnName,
  argument: ResolvedFilterColumn | null,
): string {
  if ((fn === 'MIN' || fn === 'MAX') && argument) return argument.column.dataType;
  return 'numeric';
}

/**
 * Synthesize a `ResolvedFilterColumn` for an aggregate field — either the
 * zero-arg `COUNT(*)` (`argument` is `null`) or a parameterized template
 * wrapping an already-resolved argument column. `argument.quotedField` is
 * reused verbatim (the chip's normal column-ref emit) so the result is never
 * hand-quoted here. The returned value flows through the exact same
 * `DraftFilterChip` / `buildConditionSql` path as a plain column pick.
 */
export function buildAggregateResolvedColumn(
  fn: AggregateFnName,
  argument: ResolvedFilterColumn | null,
): ResolvedFilterColumn {
  const exprText = argument ? `${fn}(${argument.quotedField})` : `${fn}(*)`;
  const column: TableExplorerColumn = {
    name: exprText,
    // Only categorizes operator/value selection; never emitted into SQL (the
    // emit path uses `quotedField` below). MIN/MAX keep the argument's type;
    // COUNT/SUM/AVG are numeric (the opaque 'numeric' marker).
    dataType: aggregateResultDataType(fn, argument),
    nullable: true,
    isPrimaryKey: false,
    isForeignKey: false,
  };
  const draft: FilterGroup = {
    field: exprText,
    conditions: [defaultDraftConditionFields(column, undefined)],
  };
  // No owning table backs an aggregate expression — `table` stays empty
  // (the shared `ResolvedFilterColumn` shape requires a string, not optional).
  return { draft, quotedField: exprText, column, table: '' };
}
